import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { clientsService } from '@/services/clients.service';
import { profilesService } from '@/services/profiles.service';
import { COUNTRIES } from '@/lib/constants/countries';
import { Currency } from '@/types';
import { Switch } from '@/components/ui/switch';

function NewClientContent() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    type: 'business' as 'business' | 'private',
    name: '',
    businessName: '',
    currency: '' as Currency | '',
    email: '',
    vatNumber: '',
    vatReverseCharge: false,
    vatReverseChargeMessage: '',
    street: '',
    city: '',
    zipCode: '',
    country: '',
    hourlyRate: '',
    defaultTaxRate: '0',
    termsAndConditions: '',
    contactInfo: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load profile and pre-populate terms and conditions and hourly rate
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await profilesService.getProfile();
        setFormData((prev) => ({
          ...prev,
          termsAndConditions: profile?.defaultTermsAndConditions || '',
          hourlyRate: profile?.defaultHourlyRate?.toString() || '',
        }));
      } catch (err) {
        console.error('Error loading profile:', err);
      }
    };
    loadProfile();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await clientsService.createClient({
        name: formData.name,
        businessName: formData.businessName || undefined,
        type: formData.type,
        currency: formData.currency || undefined,
        email: formData.email || undefined,
        vatNumber: formData.vatNumber || undefined,
        vatReverseCharge: formData.vatReverseCharge,
        vatReverseChargeMessage: formData.vatReverseChargeMessage || undefined,
        street: formData.street || undefined,
        city: formData.city || undefined,
        zipCode: formData.zipCode || undefined,
        country: formData.country || undefined,
        hourlyRate: formData.hourlyRate ? parseFloat(formData.hourlyRate) : undefined,
        defaultTaxRate: formData.defaultTaxRate ? parseFloat(formData.defaultTaxRate) : 0,
        termsAndConditions: formData.termsAndConditions || undefined,
        contactInfo: formData.contactInfo || undefined,
        notes: formData.notes || undefined,
      });
      navigate('/clients');
    } catch (err: any) {
      console.error('Error creating client:', err);
      setError(err.message || 'Failed to create client');
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <Breadcrumb items={[{ label: 'Clients', href: '/clients' }, { label: 'New Client' }]} />

      <div className="max-w-2xl">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Add New Client</h1>
        <p className="text-gray-600 mb-8">Create a new client profile</p>

        <form onSubmit={handleSubmit} className="border border-border rounded-lg p-4 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
              {error}
            </div>
          )}

          {/* Client Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Client Type *</label>
            <div className="flex rounded-lg overflow-hidden border border-gray-300">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'private' })}
                className={`flex-1 px-6 py-3 font-medium transition-colors ${
                  formData.type === 'private'
                    ? 'bg-flowky-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Private person
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'business' })}
                className={`flex-1 px-6 py-3 font-medium transition-colors ${
                  formData.type === 'business'
                    ? 'bg-flowky-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Business
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Client name (internal) *
              </label>
              <input
                id="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                placeholder="Main Client, Project ABC, etc."
              />
              <p className="text-xs text-gray-500 mt-1">Your internal reference name</p>
            </div>

            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
                {formData.type === 'business' ? 'Business name' : 'Full name'}
              </label>
              <input
                id="businessName"
                type="text"
                value={formData.businessName}
                onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                placeholder={formData.type === 'business' ? 'TechSolutions GmbH' : 'Sarah Martinez'}
              />
              <p className="text-xs text-gray-500 mt-1">Name shown on invoices</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formData.type === 'business' && (
              <>
                <div>
                  <label htmlFor="vatNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    VAT number
                  </label>
                  <input
                    id="vatNumber"
                    type="text"
                    value={formData.vatNumber}
                    onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                    placeholder="DE123456789"
                  />
                </div>

                <div className="flex items-center gap-3 pt-7">
                  <Switch
                    id="vatReverseCharge"
                    checked={formData.vatReverseCharge}
                    onCheckedChange={(isChecked) => {
                      setFormData({
                        ...formData,
                        vatReverseCharge: isChecked,
                        vatReverseChargeMessage: isChecked
                          ? 'VAT reverse charge applies under Article 44 and 196 of the EU VAT Directive. Reverse-Charge - tax liability of the recipient.'
                          : ''
                      });
                    }}
                  />
                  <label htmlFor="vatReverseCharge" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Apply VAT Reverse Charge
                  </label>
                </div>

                {formData.vatReverseCharge && (
                  <div className="md:col-span-2">
                    <label htmlFor="vatReverseChargeMessage" className="block text-sm font-medium text-gray-700 mb-2">
                      VAT Reverse Charge Message
                    </label>
                    <textarea
                      id="vatReverseChargeMessage"
                      value={formData.vatReverseChargeMessage}
                      onChange={(e) => setFormData({ ...formData, vatReverseChargeMessage: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                      placeholder="Custom VAT reverse charge message..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This message will be included in invoice notes when generating PDFs for this client.
                    </p>
                  </div>
                )}
              </>
            )}

            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                Currency
              </label>
              <select
                id="currency"
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value as Currency | '' })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              >
                <option value="">Select currency</option>
                <option value="EUR">EUR - Euro</option>
                <option value="USD">USD - US Dollar</option>
                <option value="AED">AED - UAE Dirham</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Preferred currency for invoices</p>
            </div>
          </div>

          {/* Address Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
                Street & Number
              </label>
              <input
                id="street"
                type="text"
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                placeholder="42 Hauptstraße"
              />
            </div>

            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                id="city"
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                placeholder="Munich"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                Zip Code
              </label>
              <input
                id="zipCode"
                type="text"
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                placeholder="80331"
              />
            </div>

            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <select
                id="country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent bg-white"
              >
                <option value="">Select a country</option>
                {COUNTRIES.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email invoices to (Optional)
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              placeholder="billing@techsolutions.de"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="hourlyRate" className="block text-sm font-medium text-gray-700 mb-1">
                Hourly Rate (€)
              </label>
              <input
                id="hourlyRate"
                type="number"
                min="0"
                step="0.01"
                value={formData.hourlyRate}
                onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                onKeyDown={(e) => {
                  // Prevent letters, only allow numbers, decimal point, backspace, delete, arrows, tab
                  if (
                    !/^\d$/.test(e.key) &&
                    e.key !== '.' &&
                    e.key !== 'Backspace' &&
                    e.key !== 'Delete' &&
                    e.key !== 'ArrowLeft' &&
                    e.key !== 'ArrowRight' &&
                    e.key !== 'Tab'
                  ) {
                    e.preventDefault();
                  }
                }}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                placeholder="85.00"
              />
            </div>

            <div>
              <label htmlFor="defaultTaxRate" className="block text-sm font-medium text-gray-700 mb-1">
                Default Tax Rate (%)
              </label>
              <input
                id="defaultTaxRate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData.defaultTaxRate}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  if (e.target.value === '' || (value >= 0 && value <= 100)) {
                    setFormData({ ...formData, defaultTaxRate: e.target.value });
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent letters, only allow numbers, decimal point, backspace, delete, arrows, tab
                  if (
                    !/^\d$/.test(e.key) &&
                    e.key !== '.' &&
                    e.key !== 'Backspace' &&
                    e.key !== 'Delete' &&
                    e.key !== 'ArrowLeft' &&
                    e.key !== 'ArrowRight' &&
                    e.key !== 'Tab'
                  ) {
                    e.preventDefault();
                  }
                }}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                placeholder="0"
              />
            </div>
          </div>

          <div>
            <label htmlFor="contactInfo" className="block text-sm font-medium text-gray-700 mb-1">
              Phone / Additional Contact
            </label>
            <input
              id="contactInfo"
              type="text"
              value={formData.contactInfo}
              onChange={(e) => setFormData({ ...formData, contactInfo: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              placeholder="+33 1 23 45 67 89"
            />
          </div>

          <div>
            <label htmlFor="termsAndConditions" className="block text-sm font-medium text-gray-700 mb-1">
              Terms & Conditions
            </label>
            <textarea
              id="termsAndConditions"
              rows={6}
              value={formData.termsAndConditions}
              onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              placeholder="Default terms and conditions for invoices to this client..."
            />
            <p className="text-xs text-gray-500 mt-1">Will be used as default for invoices. Leave empty to use your profile terms.</p>
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              id="notes"
              rows={4}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
              placeholder="Additional notes about this client..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Client'}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={loading}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}

export default function NewClientPage() {
  return (
    <ProtectedRoute>
      <NewClientContent />
    </ProtectedRoute>
  );
}
